import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "../shared/modal/Modal";
import Footer from "./Footer";
import { useLandscapePhone } from "../shared/useResponsive";
import { useEggBalance } from "../shared/shop/useEggBalance";
import { eggIconUrl } from "../shared/golden-eggs";
import "./section-app.scss";
import { ON_PORTAL } from "../shared/portal-env";

// Phone mode, matching the stylesheet's breakpoint (see app-responsive.scss).
const PHONE = "(max-width: 900px), (pointer: coarse)";
function usePhone(): boolean {
  const [phone, setPhone] = useState(() => typeof matchMedia === "function" && matchMedia(PHONE).matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(PHONE);
    const on = () => setPhone(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return phone;
}

// The golden egg pill at the right end of a section's top bar on a landscape
// phone: your Golden Eggs, and the way to the shop (on the shop itself, to the
// Egg Packs). It is placed in the modal's header row, after whatever the page
// put there, so the bar lays it out like any other header item.
// (data-eggchip: the shop's buy and spend celebrations fly their eggs to it.)
function EggPill() {
  const eggs = useEggBalance();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const marker = useRef<HTMLSpanElement>(null);
  const [head, setHead] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setHead(marker.current?.closest(".app-modal")?.querySelector(".app-modal-head") ?? null);
  }, []);
  const pill = (
    <button
      type="button"
      className="section-egg-pill"
      data-eggchip=""
      aria-label={eggs != null ? `${eggs} Golden Eggs. Open the shop` : "Open the shop"}
      onClick={() => navigate("/shop", { replace: pathname === "/shop" })}
    >
      <img src={eggIconUrl(44)} alt="" />
      {eggs != null ? <b>{eggs.toLocaleString()}</b> : null}
      <i aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
      </i>
    </button>
  );
  return (
    <>
      <span ref={marker} hidden />
      {head ? createPortal(pill, head) : null}
    </>
  );
}

// A shell section (News / Leaderboard / Settings) shown as a light-weight
// floating modal over the lobby: no dimming backdrop, dismissed with the back
// button or Escape, which routes back to the lobby. A click outside does NOT
// close it: these are pages that merely present like modals, and losing your
// place to a stray click feels like a bug. Each has its own URL, so the modals
// are deep-linkable and the nav highlights the active one.
export default function SectionModal({
  title,
  onClose,
  children,
  tabbed = false,
  flush = false,
  wide = false,
  pageScroll = false,
  eggs = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  // The page opens with a SectionTabs band directly under the header: the
  // header then keeps no gap below its divider (the band's hairline takes over).
  tabbed?: boolean;
  // The page is one edge-to-edge surface (the leaderboard table): no gap under
  // the header divider and the scroll area runs to the modal's bottom edge.
  flush?: boolean;
  // A data-heavy page (the admin bot leaderboard's table): a wider panel than
  // the reading-width sections.
  wide?: boolean;
  // A long page (the admin bot leaderboard's table): no scroll area inside the
  // panel. The panel grows with its content and the PAGE scrolls, at every
  // width (what phones already do for every section), so there is exactly one
  // scrollbar and the whole window's height is reading room.
  pageScroll?: boolean;
  // The egg pill in a landscape phone's top bar: only where your Golden Eggs
  // matter (the shop, your profile), not on News, Help and the like.
  eggs?: boolean;
}) {
  // Flag the body while a section is open. On touch devices the stylesheet
  // then hides the page behind the section instead of locking the body's
  // overflow: a locked body is a non-scrollable root, and Chrome on Android
  // only pulls-to-refresh when the root scroller is the thing being pulled.
  useEffect(() => {
    document.body.classList.add("app-section-open");
    return () => document.body.classList.remove("app-section-open");
  }, []);
  useEffect(() => {
    if (!pageScroll) return;
    document.body.classList.add("app-section-page");
    return () => document.body.classList.remove("app-section-page");
  }, [pageScroll]);
  // On phones the footer is not a fixed strip below the panel but the last
  // thing in the panel's own scroll: you reach it by scrolling the content.
  const phone = usePhone();
  // A phone on its side: the section is a full-screen game-app page, not a
  // panel under the website's navbar. A slim top bar (round back button, the
  // title, the page's tabs, the egg pill), the content filling the rest inside
  // the safe areas, and no navbar or footer (section-app.scss does all of it,
  // keyed on this body class).
  const app = useLandscapePhone();
  useEffect(() => {
    if (!app) return;
    document.body.classList.add("section-app");
    return () => document.body.classList.remove("section-app");
  }, [app]);
  return (
    <Modal
      title={title}
      onClose={onClose}
      dim={false}
      backButton
      closeOnBackdrop={false}
      className={"app-modal--section" + (tabbed ? " app-modal--tabbed" : "") + (flush ? " app-modal--flush" : "") + (wide ? " app-modal--wide" : "")}
    >
      {children}
      {app ? (eggs && !ON_PORTAL ? <EggPill /> : null) : phone || pageScroll ? <Footer inline /> : null}
    </Modal>
  );
}
